import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
} from "@mui/material";
import InfoTooltip from "./InfoTooltip";
import { useViewMode } from "../contexts/ViewModeContext";

const TimeLengthInput = ({
  label = "Time Length",
  value, // expects an object like { minutes: number, seconds: number } or null/undefined
  onChange,
  mandatory = false,
  instruction, // optional instruction string to show
}) => {
  // Store raw input as strings.
  const [minutes, setMinutes] = useState(
    value && typeof value.minutes === "number" ? String(value.minutes) : ""
  );
  const [seconds, setSeconds] = useState(
    value && typeof value.seconds === "number" ? String(value.seconds) : ""
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const {isViewMode} = useViewMode();

  // Update local state when props change
  useEffect(() => {
    // console.log("TimeLengthInput received value:", value);
    if (value && typeof value.minutes === "number") {
      setMinutes(String(value.minutes));
    }
    if (value && typeof value.seconds === "number") {
      setSeconds(String(value.seconds));
    }
  }, [value]);

  // Validation function using numeric values.
  const validate = (m, s) => {
    if (m === 0 && s === 0) {
      return mandatory ? `${label} is required` : "";
    }
    if (m === 0 && (s < 1 || s > 60)) {
      return `Seconds must be between 1 and 60 if minutes is 0`;
    }
    if (m > 0 && (s < 0 || s > 59)) {
      return `Seconds must be between 0 and 59 if minutes is non-zero`;
    }
    return "";
  };

  // Update parent's value based on current numeric values.
  // If there's an error, pass null.
  const updateParent = (m, s) => {
    const err = validate(m, s);
    if (err) {
      onChange?.(null);
    } else {
      onChange?.(m === 0 && s === 0 ? null : { minutes: m, seconds: s });
    }
  };

  const handleMinutesChange = (e) => {
    // Simply update raw value so user can type anything.
    setMinutes(e.target.value);
  };

  const handleSecondsChange = (e) => {
    setSeconds(e.target.value);
  };

  // Common blur handler for both fields.
  const handleBlur = () => {
    let newMinutes = minutes;
    let newSeconds = seconds;
    // If the input is not empty and contains any non-digit characters, clear it.
    if (newMinutes !== "" && !/^\d+$/.test(newMinutes)) {
      setErrorMessage("Minutes must be numeric");
      newMinutes = "";
      setMinutes("");
    }
    if (newSeconds !== "" && !/^\d+$/.test(newSeconds)) {
      setErrorMessage("Seconds must be numeric");
      newSeconds = "";
      setSeconds("");
    }
    // Convert empty strings to 0.
    const m = newMinutes === "" ? 0 : parseInt(newMinutes, 10);
    const s = newSeconds === "" ? 0 : parseInt(newSeconds, 10);
    const err = validate(m, s);
    setErrorMessage(err);
    updateParent(m, s);
  };

  // Popover for instructions.
  const handleInstructionClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleInstructionClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className="annotation-form-field-container">
      {/* Standardized label */}
      <div className="annotation-form-field-label">
        <span className={`label-text ${mandatory ? 'mandatory' : ''}`}>
          {label}
        </span>
        {mandatory && <span className="required-asterisk">*</span>}
        {instruction && <InfoTooltip instruction={instruction} />}
      </div>

      {/* Input fields */}
      <div className="annotation-form-field-input">
        <Box display="flex" alignItems="center" gap={1}>
        <TextField
          type="text"
          value={minutes}
          onChange={handleMinutesChange}
          onBlur={handleBlur}
          inputProps={{ inputMode: "numeric", pattern: "\\d*" }}
          size="small"
          sx={{ width: '30%' }}
          error={Boolean(errorMessage)}
          disabled={isViewMode}
        />
        <Typography>min</Typography>
        <TextField
          type="text"
          value={seconds}
          onChange={handleSecondsChange}
          onBlur={handleBlur}
          inputProps={{ inputMode: "numeric", pattern: "\\d*" }}
          size="small"
          error={Boolean(errorMessage)}
          sx={{ width: '30%' }}  
          disabled={isViewMode}
        />
        <Typography>s</Typography>
      </Box>

      {/* Error Message */}
      {errorMessage && (
        <Typography color="error" variant="caption">
          {errorMessage}
        </Typography>
      )}
      </div>
    </div>
  );
};

export default TimeLengthInput;
